<template>
  <el-dialog
    :title="title"
    :visible="value"
    width="90%"
    @open="onOpen"
    @close="onClose"
    :close-on-click-modal="false"
    :append-to-body="true"
  >
    <div class="image-cropper">
      <!-- 容器添加固定宽高占位，避免初始化时尺寸为0 -->
      <div class="cropper-container" ref="container">
        <!-- 图片加载状态显示，避免空白 -->
        <div v-if="!isImgLoaded && !imgError" class="img-loading">
          图片加载中...
        </div>
        <!-- 图片加载失败提示 -->
        <div v-if="imgError" class="img-error">图片加载失败，请刷新重试</div>
        <img
          ref="sourceImage"
          :src="currentMember.avatar"
          alt="裁剪源图"
          @load="handleImgLoad"
          @error="handleImgError"
          crossorigin="anonymous"
          class="source-image"
          v-show="isImgLoaded"
          draggable="false"
          ondragstart="return false"
          onselectstart="return false"
        />

        <!-- 选择框：确保初始样式可见，修复transform-origin -->
        <div
          ref="selector"
          class="selector"
          :class="{ 'circle-selector': isCircle, hidden: !isImgLoaded }"
          :style="{
            width: `${selectorWidth}px`,
            height: `${selectorHeight}px`,
            transform: `translate(${selectorLeft}px, ${selectorTop}px)`,
            transformOrigin: 'top left', // 关键：位移/缩放原点统一为左上角
            willChange: isDragging || isResizing ? 'transform, width' : 'none',
          }"
          @mousedown="startDrag"
        >
          <div class="move-handle" :class="{ 'circle-handle': isCircle }"></div>
          <div class="resize-handle" @mousedown.stop="startResize"></div>
        </div>
      </div>

      <div class="cropper-info" v-if="showInfo && isImgLoaded">
        裁剪形状：{{ isCircle ? "圆形" : "矩形" }} | 比例：{{ aspectRatio }}
      </div>
    </div>
    <div slot="footer">
      <el-button plain @click="onClose">关闭</el-button>
      <el-button type="primary" @click="cropImage" :disabled="!isImgLoaded"
        >保存所选区域为头像</el-button
      >
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: "ImgCropper",
  props: {
    value: { type: Boolean, default: false },
    currentMember: { type: Object, default: () => ({}), required: true },
    isCircle: { type: Boolean, default: false },
    title: { type: String, default: "" },
    aspectRatio: {
      type: String,
      default: "1:1",
      validator: (val) => {
        const reg = /^\d+:\d+$/;
        if (!reg.test(val)) {
          console.error("aspectRatio 格式错误，应传入如 '1:1'、'4:3' 的字符串");
          return false;
        }
        const [w, h] = val.split(":").map(Number);
        return w > 0 && h > 0;
      },
    },
    showInfo: { type: Boolean, default: false },
  },
  data() {
    return {
      selectorLeft: 0,
      selectorTop: 0,
      selectorWidth: 0, // 初始宽度设为0，避免初始位置异常
      isDragging: false,
      isResizing: false,
      startX: 0,
      startY: 0,
      startWidth: 0,
      containerRect: null, // 容器实际尺寸（包含图片尺寸）
      ratioW: 1,
      ratioH: 1,
      containerMaxLeft: 0,
      containerMaxTop: 0,
      isImgLoaded: false, // 图片加载状态标记
      imgError: false, // 图片加载失败标记
    };
  },
  computed: {
    selectorHeight() {
      return Math.round(this.selectorWidth * this.ratioH);
    },
    maxSelectorWidth() {
      if (!this.containerRect) return 300;
      const maxByHeight = this.containerRect.height / this.ratioH;
      return Math.min(this.containerRect.width, maxByHeight); // 限制最大宽度400px，避免过大
    },
  },
  watch: {
    aspectRatio: {
      immediate: true,
      handler(val) {
        const [w, h] = val.split(":").map(Number);
        this.ratioW = w / h;
        this.ratioH = h / w;
        if (this.isImgLoaded) {
          // 图片加载完成后再调整
          this.constrainSelectorSize();
          this.constrainSelectorPosition();
        }
      },
    },
    selectorWidth: {
      immediate: false,
      handler(val) {
        const minWidth = 50;
        if (val < minWidth) this.selectorWidth = minWidth;
        if (val > this.maxSelectorWidth)
          this.selectorWidth = this.maxSelectorWidth;
        if (this.isImgLoaded) {
          this.updateContainerBounds(); // 同步边界
          this.constrainSelectorPosition();
        }
      },
    },
    // 弹框显示时，强制重新计算容器尺寸（解决弹框渲染延迟问题）
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.isImgLoaded) {
          this.$nextTick(() => {
            // 确保DOM渲染完成
            setTimeout(() => {
              this.updateContainerRect();
              this.updateContainerBounds();
              this.resetSelectorPosition(); // 重置选择框到中心
            }, 100); // 兼容弹框动画延迟
          });
        }
      },
    },
  },
  methods: {
    onOpen() {
      // 弹框打开时，若图片已加载，直接初始化选择框
      document.addEventListener("mousemove", this.handleAction);
      document.addEventListener("mouseup", this.stopAction);

      if (this.isImgLoaded) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.updateContainerRect();
            this.resetSelectorPosition();
          }, 100); // 兼容弹框动画延迟
        });
      }
    },
    onClose() {
      document.removeEventListener("mousemove", this.handleAction);
      document.removeEventListener("mouseup", this.stopAction);
      this.$emit("input", false);
    },
    // 图片加载完成后初始化（关键：确保图片尺寸已知）
    handleImgLoad() {
      this.isImgLoaded = true;
      this.imgError = false;
      // 强制等待DOM更新（确保图片尺寸已渲染）
      this.$nextTick(() => {
        this.updateContainerRect(); // 先获取图片实际尺寸
        if (this.containerRect) {
          // 确保容器尺寸有效
          this.resetSelectorPosition(); // 重置选择框位置和尺寸
        } else {
          // 异常情况：容器尺寸获取失败，手动设置默认尺寸
          const img = this.$refs.sourceImage;
          this.containerRect = {
            width: img.offsetWidth || 300,
            height: img.offsetHeight || 300,
            left: img.getBoundingClientRect().left || 0,
            top: img.getBoundingClientRect().top || 0,
          };
          this.resetSelectorPosition();
        }
      });
    },
    handleImgError() {
      this.imgError = true;
      this.isImgLoaded = false;
      console.error("头像图片加载失败");
    },
    // 强制更新容器尺寸（包含图片实际尺寸）
    updateContainerRect() {
      const container = this.$refs.container;
      const img = this.$refs.sourceImage;
      if (!container || !img) return;

      // 容器尺寸 = 图片实际显示尺寸（避免容器比图片大导致选择框偏移）
      const imgRect = img.getBoundingClientRect();
      this.containerRect = {
        width: imgRect.width,
        height: imgRect.height,
        left: imgRect.left,
        top: imgRect.top,
      };

      // 同步容器样式（确保容器与图片尺寸一致）
      container.style.width = `${imgRect.width + 20}px`;
      container.style.height = `${imgRect.height + 20}px`;
    },
    // 更新容器边界（选择框最大可移动范围）
    updateContainerBounds() {
      if (!this.containerRect) return;
      this.containerMaxLeft = this.containerRect.width - this.selectorWidth;
      this.containerMaxTop = this.containerRect.height - this.selectorHeight;
    },
    // 重置选择框到图片中心（确保打开时可见）
    resetSelectorPosition() {
      if (!this.containerRect) return;

      console.log("容器尺寸：", this.containerRect); // 调试用
      // 1. 先更新最大可移动范围
      this.updateContainerBounds();
      // 2. 计算选择框尺寸（确保不超过容器）
      this.selectorWidth = Math.min(
        Math.max(50, this.containerRect.width / 2), // 最小50px，默认1/2图片宽
        this.maxSelectorWidth
      );
      // 3. 计算居中位置（确保在容器内）
      this.selectorLeft = Math.max(
        0,
        Math.min(
          (this.containerRect.width - this.selectorWidth) / 2,
          this.containerMaxLeft
        )
      );
      this.selectorTop = Math.max(
        0,
        Math.min(
          (this.containerRect.height - this.selectorHeight) / 2,
          this.containerMaxTop
        )
      );

      console.log("选择框尺寸：", this.selectorWidth, this.selectorHeight); // 调试用
      console.log("选择框位置：", this.selectorLeft, this.selectorTop); // 调试用

      // 同步边界
      this.updateContainerBounds();
    },
    startDrag(e) {
      e.stopPropagation();
      this.isDragging = true;
      // 计算鼠标相对于选择框左上角的偏移（解决transform定位导致的偏移问题）
      const selectorRect = this.$refs.selector.getBoundingClientRect();
      this.startX = e.pageX - selectorRect.left;
      this.startY = e.pageY - selectorRect.top;
      this.$refs.selector.style.cursor = "grabbing";
    },
    startResize(e) {
      e.stopPropagation();
      this.isResizing = true;
      this.startX = e.pageX;
      this.startY = e.pageY;
      this.startWidth = this.selectorWidth;
      this.$refs.selector.style.cursor = "nwse-resize";
    },
    handleAction(e) {
      if (!this.isDragging && !this.isResizing) return;
      if (!this.isImgLoaded) return; // 图片未加载完成，不处理

      e.preventDefault();
      const pageX = e.pageX;
      const pageY = e.pageY;

      if (this.isDragging) {
        // 计算新位置（基于容器左上角定位，而非文档）
        const containerLeft = this.containerRect.left;
        const containerTop = this.containerRect.top;

        let newLeft = pageX - containerLeft - this.startX;
        let newTop = pageY - containerTop - this.startY;

        // 边界限制
        newLeft = Math.max(0, Math.min(newLeft, this.containerMaxLeft));
        newTop = Math.max(0, Math.min(newTop, this.containerMaxTop));

        // 仅在值变化时更新
        if (newLeft !== this.selectorLeft) this.selectorLeft = newLeft;
        if (newTop !== this.selectorTop) this.selectorTop = newTop;
      } else if (this.isResizing) {
        const deltaX = pageX - this.startX;
        const deltaY = (pageY - this.startY) / this.ratioH;
        const delta = Math.min(deltaX, deltaY);

        let newWidth = this.startWidth + delta;
        newWidth = Math.max(50, Math.min(newWidth, this.maxSelectorWidth));

        if (newWidth !== this.selectorWidth) {
          this.selectorWidth = newWidth;
          this.updateContainerBounds(); // 缩放后同步边界
        }
      }
    },
    stopAction() {
      this.isDragging = false;
      this.isResizing = false;
      this.$refs.selector.style.cursor = "move";
    },
    constrainSelectorPosition() {
      if (!this.containerRect) return;
      this.selectorLeft = Math.max(
        0,
        Math.min(this.selectorLeft, this.containerMaxLeft)
      );
      this.selectorTop = Math.max(
        0,
        Math.min(this.selectorTop, this.containerMaxTop)
      );
    },
    constrainSelectorSize() {
      this.selectorWidth = Math.min(this.selectorWidth, this.maxSelectorWidth);
      this.selectorWidth = Math.max(50, this.selectorWidth);
    },
    cropImage() {
      if (!this.isImgLoaded) return;

      const img = this.$refs.sourceImage;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const width = this.selectorWidth;
      const height = this.selectorHeight;
      canvas.width = width;
      canvas.height = height;

      // 计算裁剪比例（基于图片原始尺寸和显示尺寸）
      const scaleX = img.naturalWidth / img.offsetWidth;
      const scaleY = img.naturalHeight / img.offsetHeight;

      if (this.isCircle) {
        ctx.beginPath();
        ctx.arc(
          width / 2,
          height / 2,
          Math.min(width, height) / 2,
          0,
          2 * Math.PI
        );
        ctx.clip();
      }

      requestAnimationFrame(() => {
        ctx.drawImage(
          img,
          this.selectorLeft * scaleX,
          this.selectorTop * scaleY,
          width * scaleX,
          height * scaleY,
          0,
          0,
          width,
          height
        );

        const croppedUrl = canvas.toDataURL("image/png");
        this.$set(this.currentMember, "editAvatar", croppedUrl);
        this.$emit("crop-success", croppedUrl, {
          width,
          height,
          isCircle: this.isCircle,
          aspectRatio: this.aspectRatio,
        });

        this.onClose();
      });
    },
  },
  unmounted() {
    this.onClose();
  },
};
</script>

<style scoped lang="scss">
.image-cropper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px;
  position: relative;
}

.cropper-container {
  position: relative;
  border: 1px solid #ccc;
  display: inline-block;
  cursor: default;
  background-color: #f5f5f5;
  overflow: hidden;
  transition: width 0.2s ease, height 0.2s ease;
  padding: 10px;
}

.img-loading {
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 14px;
}

.img-error {
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff4400;
  font-size: 14px;
}

.source-image {
  max-width: 800px;
  max-height: 600px;
  display: block;
  min-width: 60px;
  image-rendering: -webkit-optimize-contrast;
  /* 禁止选择/复制 */
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.selector {
  position: absolute;
  border: 2px dashed #42b983;
  background-color: rgba(66, 185, 131, 0.1);
  cursor: move;
  box-sizing: border-box;
  transition: none;
  z-index: 9999; // 提高层级，确保不被遮挡
  opacity: 1; // 强制显示
  left: 10px;
  top: 10px;
}

.circle-selector {
  border-radius: 50%;
  border-style: solid;
  background-color: rgba(255, 68, 0, 0.15);
}

.hidden {
  display: none; // 图片未加载时隐藏选择框
}

.move-handle {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  transform: translate(-50%, -50%);
  background-color: rgba(66, 185, 131, 0.3);
  border-radius: 50%;
  pointer-events: none;
}

.circle-handle {
  background-color: rgba(66, 185, 131, 0.5);
  box-shadow: 0 0 4px rgba(66, 185, 131, 0.8);
}

.resize-handle {
  position: absolute;
  right: -5px;
  bottom: -5px;
  width: 14px;
  height: 14px;
  background-color: #42b983;
  border: 2px solid white;
  border-radius: 3px;
  cursor: nwse-resize;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
  z-index: 11;
  transform: translate(0, 0);
}

.cropper-info {
  font-size: 12px;
  color: #666;
  padding: 4px 8px;
  background-color: #f9f9f9;
  border-radius: 4px;
}
</style>
